import React, { useMemo } from 'react';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

const formatValue = (value) => {
  const abs = Math.abs(Number(value) || 0);
  if (abs >= 1000) return `${(abs / 1000).toFixed(1)} k`;
  return abs.toFixed(1);
};

const formatSignedValue = (value) => {
  const n = Number(value) || 0;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)} k`;
  return `${sign}${abs.toFixed(1)}`;
};

const getToneClasses = (value) => {
  const n = Number(value) || 0;
  if (n > 0) return { wrap: 'bg-green-50', text: 'text-green-700' };
  if (n < 0) return { wrap: 'bg-red-50', text: 'text-red-700' };
  return { wrap: 'bg-gray-100', text: 'text-gray-900' };
};

const FLOW_COLOR_IN = '#4ade80';
const FLOW_COLOR_OUT = '#f87171';
const FLOW_CHIP_IN = '#16a34a';
const FLOW_CHIP_OUT = '#dc2626';

const getDaysInMonth = (year, month01) => {
  const y = Number(year);
  const m = Number(month01);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return 0;
  return new Date(y, m, 0).getDate();
};

const parseHyphenList = (value) => {
  if (value == null) return [];
  const s = String(value).trim();
  if (!s) return [];
  return s
    .split('-')
    .map((x) => x.trim())
    .filter(Boolean);
};

const getTotalDaysForSelection = (yearsValue, monthsValue) => {
  const years = parseHyphenList(yearsValue);
  const months = parseHyphenList(monthsValue);
  if (!years.length || !months.length) return 0;
  let total = 0;
  for (const y of years) {
    for (const m of months) {
      total += getDaysInMonth(y, m);
    }
  }
  return total;
};

const CardBudgetConsumed = ({
  boardData,
  loading = false,
  filterLabel,
  selectedType = 'Out',
  flows = [],
  selectedYears,
  selectedMonths,
}) => {
  const flowBarColor = selectedType === 'In' ? FLOW_COLOR_IN : FLOW_COLOR_OUT;
  const flowChipColor = selectedType === 'In' ? FLOW_CHIP_IN : FLOW_CHIP_OUT;
  const summary = boardData?.summary;
  const totalFlow = summary?.total_flow != null ? Number(summary.total_flow) : 0;
  const totalBudget = summary?.total_budget != null ? Number(summary.total_budget) : 0;
  const achievementPct = summary?.achievement_pct != null ? Number(summary.achievement_pct) : 0;
  // Display magnitudes for Out (backend may return std negatives)
  const flowDisplay = selectedType === 'Out' ? Math.abs(totalFlow) : totalFlow;
  const budgetDisplay = selectedType === 'Out' ? Math.abs(totalBudget) : totalBudget;
  const diff = budgetDisplay - flowDisplay;

  const pctClamped = Math.min(100, Math.max(0, achievementPct));
  const hasData = totalBudget !== 0 || totalFlow !== 0;

  const daysTotal = getTotalDaysForSelection(selectedYears, selectedMonths);
  const daysWithFlow = useMemo(() => {
    if (!Array.isArray(flows) || flows.length === 0) return 0;
    const set = new Set();
    for (const f of flows) {
      if (f?.date) set.add(String(f.date));
    }
    return set.size;
  }, [flows]);
  const daysPct = daysTotal > 0 ? (daysWithFlow / daysTotal) * 100 : 0;

  if (loading) {
    return (
      <div className="flex flex-col h-full w-full">
        <div className="min-h-[200px] w-full bg-white shadow-lg rounded-xl p-3 relative overflow-hidden flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-6 bg-gray-400 rounded animate-pulse w-48" />
            <div className="h-5 bg-gray-300 rounded animate-pulse w-16" />
          </div>
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 rounded-full animate-pulse w-full" />
            <div className="flex gap-2">
              <div className="flex-1 h-14 bg-gray-200 rounded-lg animate-pulse" />
              <div className="flex-1 h-14 bg-gray-200 rounded-lg animate-pulse" />
              <div className="flex-1 h-14 bg-gray-200 rounded-lg animate-pulse" />
            </div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-40" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
            <SpinnerLoad />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="bg-white shadow-lg rounded-xl p-3 flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-md font-semibold">Budget Consumed</span>
          {filterLabel != null && filterLabel !== '' && (
            <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
              {filterLabel}
            </span>
          )}
        </div>

        {hasData ? (
          <div className="flex-1 min-h-0 flex flex-col pt-3 pb-4">
            {/* Percentage bar: flow vs budget — chip outside overflow so it can overhang */}
            <div className="relative min-h-[34px] flex items-center w-full">
              <div className="h-3 w-full min-w-0 rounded-full bg-gray-200 overflow-hidden flex shrink-0">
                <div
                  className="h-full flex-shrink-0 transition-all duration-300 rounded-l-full"
                  style={{
                    width: `${pctClamped}%`,
                    minWidth: 0,
                    backgroundColor: flowBarColor,
                    borderTopRightRadius: pctClamped >= 100 ? 9999 : 0,
                    borderBottomRightRadius: pctClamped >= 100 ? 9999 : 0,
                  }}
                />
              </div>
              <div
                className="absolute top-1/2 z-0 pointer-events-none"
                style={
                  pctClamped <= 0
                    ? { left: 0, transform: 'translate(0, -50%)' }
                    : pctClamped >= 100
                    ? { right: 0, transform: 'translate(0, -50%)' }
                    : {
                        left: `${pctClamped}%`,
                        transform: 'translate(-50%, -50%)',
                      }
                }
              >
                <span
                  className="text-xs font-bold text-white rounded-md px-2 py-1 whitespace-nowrap shadow-sm"
                  style={{ backgroundColor: flowChipColor }}
                >
                  {achievementPct.toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Absolute values: flow | budget (vertically centered when card expands) */}
            <div className="flex-1 min-h-0 flex items-center py-1">
              <div className="grid grid-cols-3 gap-2 w-full">
                <div className="bg-gray-100 rounded-lg px-3 py-2 flex flex-col items-center text-center">
                  <div className="text-lg font-bold text-gray-900">{formatValue(flowDisplay)}</div>
                  <div className="text-xs text-gray-600">flow</div>
                </div>
                <div className="bg-gray-100 rounded-lg px-3 py-2 flex flex-col items-center text-center">
                  <div className="text-lg font-bold text-gray-900">{formatValue(budgetDisplay)}</div>
                  <div className="text-xs text-gray-600">budget</div>
                </div>
                <div
                  className={`${getToneClasses(diff).wrap} rounded-lg px-3 py-2 flex flex-col items-center text-center`}
                >
                  <div className={`text-lg font-bold ${getToneClasses(diff).text}`}>
                    {formatSignedValue(diff)}
                  </div>
                  <div className="text-xs text-gray-600">diff</div>
                </div>
              </div>
            </div>

            {/* Days (hardcoded for now) */}
            <div className="mt-1 text-base font-semibold text-gray-700 text-center">
              {daysWithFlow} of {daysTotal} days ({daysPct.toFixed(1)}%)
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 py-8 flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-500 text-sm font-medium">No data available</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardBudgetConsumed;
